import {
  createFileRoute,
  useNavigate,
  useRouteContext,
  useRouter,
} from "@tanstack/react-router";
import { useEffect, type JSX } from "react";

import { graphql } from "@repo/graphql-types";
import Clock from "@repo/icons/default/Clock";
import DoctorWithStethoscope from "@repo/icons/default/DoctorWithStethoscope";
import Left from "@repo/icons/default/Left";
import Paint from "@repo/icons/default/Paint";
import Share from "@repo/icons/default/Share";

import {
  EmptyStateBody,
  EmptyStateButton,
  EmptyStateDescription,
  EmptyStateImage,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL } from "@/hooks/use-graphql";
import {
  isDomainRegistrationError,
  StatusDominioRegistro,
} from "@/lib/domain-registration-status";
import { ENTITY_NOT_MANAGED_BY_PROVIDER } from "@/routes/custom-website/confirmation/domain-error-content";

const UsuarioDominioRegistroQuery = graphql(`
  query UsuarioDominioRegistro($codUsuario: Int!) {
    usuariosDominiosRegistros(
      where: { Usuario: { codUsuario: { _eq: $codUsuario } }, ativo: { _eq: true } }
    ) {
      codStatus
      dominio
    }
  }
`);

const CustomWebsitePage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useRouteContext({ from: "__root__" });

  const queryResult = useGraphQL(UsuarioDominioRegistroQuery, {
    codUsuario: user.codUsuario,
  });

  const dominioRegistro = queryResult.data?.usuariosDominiosRegistros[0];

  useEffect(() => {
    if (!dominioRegistro) {
      return;
    }

    const redirect = isDomainRegistrationError(dominioRegistro.codStatus)
      ? navigate({
          to: "/custom-website/confirmation/error",
          search: {
            errorCode:
              dominioRegistro.codStatus ===
              StatusDominioRegistro.FalhaRegistroTitularidade
                ? ENTITY_NOT_MANAGED_BY_PROVIDER
                : undefined,
          },
        })
      : navigate({
          to: "/custom-website/confirmation",
          search: { dominio: dominioRegistro.dominio },
        });

    // eslint-disable-next-line no-console -- off
    redirect.catch((error) => console.error(error));
  }, [dominioRegistro, navigate]);

  const handleClick = async (): Promise<void> => {
    await navigate({
      to: "/custom-website/form",
    });
  };

  const handleDeclineClick = (): void => {
    router.history.back();
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Site personalizado" align="center" />
      </HeaderRoot>
      <Page>
        <div className="flex flex-col justify-center items-center">
          <EmptyStateRoot>
            <EmptyStateImage
              icon={DoctorWithStethoscope}
              alt="Nenhum período garantido ativo"
              className="w-full h-[25vh]"
            />
            <EmptyStateTitle>Site Personalizado</EmptyStateTitle>
            <EmptyStateDescription>
              Tenha seu próprio site profissional, com domínio e layout personalizados do
              seu jeito e sem nenhum custo adicional.
            </EmptyStateDescription>
            <EmptyStateBody>
              <ul className="flex flex-col gap-2 px-4 py-6 text-sm font-normal text-neutral-600">
                <li className="flex gap-2">
                  <Share className="fill-primary-400" size={24} />
                  <span className="self-end">
                    Compartilhe suas experiências e sua trajetória profissional
                  </span>
                </li>
                <li className="flex gap-2">
                  <Clock className="fill-primary-400 self-center" size={24} />
                  <span className="">Permita que pacientes agendem pelo seu site</span>
                </li>
                <li className="flex gap-2">
                  <Paint className="fill-primary-400" size={24} />
                  <span className="self-end">Personalize em poucos passos</span>
                </li>
              </ul>
            </EmptyStateBody>
            <EmptyStateButton
              onClick={() => {
                void handleClick();
              }}
              fullWidth
            >
              Quero ter meu Site
            </EmptyStateButton>
            <button
              type="button"
              onClick={handleDeclineClick}
              className="text-sm text-neutral-400 underline"
            >
              Não quero um site personalizado
            </button>
          </EmptyStateRoot>
        </div>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/custom-website/")({
  component: CustomWebsitePage,
});
